<template>
  <form @submit.prevent="submitPersonalId" class="grid gap-4">
    <div class>
      <div class="gap-4 flex justify-items-start flex-wrap">
        <radio-button
          label="Aadhaar/आधार"
          name="address-proof"
          id="aadhaar"
          :required="true"
          value="Aadhaar"
          @change="saveAddressProofType"
          :checked="true"
        />
        <radio-button
          label="Voter ID/मतदाता पहचान पत्र"
          name="address-proof"
          id="voter-id"
          :required="true"
          value="Voter ID"
          @change="saveAddressProofType"
        />
        <radio-button
          label="Passport/पासपोर्ट"
          name="address-proof"
          id="passport"
          :required="true"
          value="Passport"
          @change="saveAddressProofType"
        />
        <radio-button
          label="Driving License/ड्राइविंग लाइसेंस"
          name="address-proof"
          id="driving-license"
          :required="true"
          value="Driving License"
          @change="saveAddressProofType"
        />
      </div>
    </div>
    <div
      class="grid md:grid-cols-2 gap-x-10 md:justify-start w-full mt-2 gap-2"
      v-if="addressProofType === 'Aadhaar'"
    >
      <p class="col-span-full text-xs text-gray-600">Upload Aadhaar Card Image</p>
      <div
        class="bg-white grid justify-items-center p-6 gap-5 items-start rounded-md content-start"
      >
        <img
          src="../assets/aadhaar-front-illustration.png"
          alt="Aadhaar Card"
          width="137"
          height="84"
        />
        <p class="block">Front Image</p>
        <custom-button
          variant="primary"
          class="relative"
          type="button"
          :loading="isUploadingFront"
          :disabled="isUploadingBack || isUploadingFront"
          size="small"
        >
          <input
            type="file"
            name="upload-aadhaar-front"
            id="upload-aadhaar-front"
            class="input-file"
            :required="!documentListUploaded?.aadhaarFrontUploaded"
            @change="uploadAadhaarFront"
            accept="image/*, .pdf"
            ref="aadhaarFront"
          />
          Upload File
        </custom-button>
        <template v-if="frontFile || documentListUploaded?.aadhaarFrontUploaded">
          <hr class="w-full" />
          <div class="flex items-center justify-center gap-4 w-full">
            <img
              src="../assets/radio-check.svg"
              alt="Green Check"
              class="w-4"
              width="15"
              height="15"
            />
            <p class="text-successColor">Uploaded</p>
          </div>
        </template>
      </div>
      <div
        class="bg-white grid justify-items-center p-6 gap-5 items-start rounded-md content-start"
      >
        <img
          src="../assets/aadhaar-back-illustration.png"
          alt="Aadhaar Card"
          width="140"
          height="86"
        />
        <p class="block">Back Image</p>
        <custom-button
          variant="primary"
          class="relative"
          type="button"
          :loading="isUploadingBack"
          :disabled="isUploadingBack || isUploadingFront"
          size="small"
        >
          <input
            type="file"
            name="upload-aadhaar-back"
            id="upload-aadhaar-back"
            class="input-file"
            :required="!documentListUploaded?.aadhaarBackUploaded"
            @change="uploadAadhaarBack"
            accept="image/*, .pdf"
            ref="aadhaarBack"
          />
          Upload File
        </custom-button>
        <template v-if="backFile || documentListUploaded?.aadhaarBackUploaded">
          <hr class="w-full" />
          <div class="flex items-center justify-center gap-4 w-full">
            <img
              src="../assets/radio-check.svg"
              alt="Green Check"
              class="w-4"
              width="15"
              height="15"
            />
            <p class="text-successColor">Uploaded</p>
          </div>
        </template>
      </div>
    </div>
    <template v-if="addressProofType === 'Driving License'">
      <div class="bg-white grid justify-items-center p-6 mt-2 gap-5 w-full">
        <img
          src="../assets/driving-license-illustration.png"
          alt="Driving License"
          width="201"
          height="152"
        />
        <p class="block">Please upload your Driving License</p>
        <custom-button
          variant="primary"
          class="relative"
          type="button"
          :loading="isUploading"
          size="small"
        >
          <input
            type="file"
            name="driving-license"
            id="driving-license"
            class="input-file"
            :required="!documentListUploaded?.drivingLicenseUploaded"
            accept="image/*, .pdf"
            @change="uploadDrivingLicense"
            ref="drivingLicense"
          />
          Upload File
        </custom-button>
        <template v-if="file || documentListUploaded?.drivingLicenseUploaded">
          <hr class="w-full" />
          <div class="flex items-center justify-center gap-4 w-full">
            <img
              src="../assets/radio-check.svg"
              alt="Green Check"
              class="w-4"
              width="15"
              height="15"
            />
            <p class="text-successColor">Uploaded</p>
          </div>
        </template>
      </div>
      <div class="grid grid-cols-2 gap-x-10 justify-start w-full mt-2">
        <div class="label mt-2">
          <label for="driving-license">Driving License Number</label>
          <input
            type="text"
            name="driving-license"
            id="driving-license"
            class="input"
            placeholder="Enter Driving License Number"
            required
            :value="params.personalInfo.drivingLicense?.toUpperCase()"
            @input="
              params.personalInfo.drivingLicense = $event.target.value
                .toUpperCase()
                .trim()
            "
          />
        </div>
        <div class="label mt-2">
          <label for="dob">Date of Birth</label>
          <input
            type="date"
            name="dob"
            placeholder="DD/MM/YYYY"
            v-model="params.personalInfo.dob"
            class="input uppercase"
            required
            :max="maxDate"
            min="1900-01-01"
          />
        </div>
      </div>
    </template>
    <template v-if="addressProofType === 'Voter ID'">
      <div class="bg-white grid justify-items-center p-6 mt-2 gap-5 w-full">
        <img src="../assets/voter-id-illustration.png" alt="Voter ID" width="142" height="171" />
        <p class="block">Please upload your Voter ID</p>
        <custom-button
          variant="primary"
          class="relative"
          type="button"
          :loading="isUploading"
          size="small"
        >
          <input
            type="file"
            name="upload-voter-id"
            id="upload-voter-id"
            class="input-file"
            :required="!documentListUploaded?.voterIdUploaded"
            @change="uploadVoterId"
            accept="image/*, .pdf"
            ref="voterId"
          />
          Upload File
        </custom-button>
        <template v-if="file || documentListUploaded?.voterIdUploaded">
          <hr class="w-full" />
          <div class="flex items-center justify-center gap-4 w-full">
            <img
              src="../assets/radio-check.svg"
              alt="Green Check"
              class="w-4"
              width="15"
              height="15"
            />
            <p class="text-successColor">Uploaded</p>
          </div>
        </template>
      </div>
      <div class="label mt-2">
        <label for="voter-id">Voter ID (Ex. ABC1234567)</label>
        <input
          type="text"
          name="voter-id"
          id="voter-id"
          class="input"
          placeholder="Enter Voter ID"
          required
          pattern="^([a-zA-Z]){3}([0-9]){7}?$"
          maxlength="10"
          minlength="10"
          :value="params.personalInfo.voterId?.toUpperCase()"
          @input="
            params.personalInfo.voterId = $event.target.value
              .toUpperCase()
              .trim()
          "
        />
      </div>
    </template>
    <div
      class="grid md:grid-cols-2 gap-x-10 md:justify-start w-full mt-2 gap-2"
      v-if="addressProofType === 'Passport'"
    >
      <p class="col-span-full text-xs text-gray-600">Upload Passport Image</p>
      <div
        class="bg-white grid justify-items-center p-6 gap-5 items-start rounded-md content-start"
      >
        <img
          src="../assets/passport-front-illustration.png"
          alt="Passport"
          width="170"
          height="113"
        />
        <p class="block">Front Image</p>
        <custom-button
          variant="primary"
          class="relative"
          type="button"
          :loading="isUploadingFront"
          :disabled="isUploadingBack || isUploadingFront"
          size="small"
        >
          <input
            type="file"
            name="upload-passport-front"
            id="upload-passport-front"
            class="input-file"
            :required="!documentListUploaded?.passportFrontUploaded"
            @change="uploadPassportFront"
            accept="image/*, .pdf"
            ref="passportFront"
          />
          Upload File
        </custom-button>
        <template v-if="frontFile || documentListUploaded?.passportFrontUploaded">
          <hr class="w-full" />
          <div class="flex items-center justify-center gap-4 w-full">
            <img
              src="../assets/radio-check.svg"
              alt="Green Check"
              class="w-4"
              width="15"
              height="15"
            />
            <p class="text-successColor">Uploaded</p>
          </div>
        </template>
      </div>
      <div
        class="bg-white grid justify-items-center p-6 gap-5 items-start rounded-md content-start"
      >
        <img src="../assets/passport-back-illustration.png" alt="Passport" width="170" height="114" />
        <p class="block">Back Image</p>
        <custom-button
          variant="primary"
          class="relative"
          type="button"
          :loading="isUploadingBack"
          :disabled="isUploadingBack || isUploadingFront"
          size="small"
        >
          <input
            type="file"
            name="upload-passport-back"
            id="upload-passport-back"
            class="input-file"
            :required="!documentListUploaded?.passportBackUploaded"
            @change="uploadPassportBack"
            accept="image/*, .pdf"
            ref="passportBack"
          />
          Upload File
        </custom-button>
        <template v-if="backFile || documentListUploaded?.passportBackUploaded">
          <hr class="w-full" />
          <div class="flex items-center justify-center gap-4 w-full">
            <img
              src="../assets/radio-check.svg"
              alt="Green Check"
              class="w-4"
              width="15"
              height="15"
            />
            <p class="text-successColor">Uploaded</p>
          </div>
        </template>
      </div>
      <div class="label mt-2">
        <label for="passport-number">Passport Number</label>
        <input
          type="text"
          name="passport-number"
          id="passport-number"
          class="input"
          placeholder="Enter Passport Number"
          required
          :value="params.personalInfo.passport?.toUpperCase()"
          @input="
            params.personalInfo.passport = $event.target.value
              .toUpperCase()
              .trim()
          "
        />
      </div>
    </div>
    <custom-button
      variant="primary"
      type="submit"
      class="md:justify-self-center"
      :loading="loading"
      :disabled="isUploading || isUploadingBack || isUploadingFront"
    >Continue</custom-button>
  </form>
  <error-message-popup
    v-if="errorPopupMessage"
    @close-modal="errorPopupMessage = undefined"
    :errorPopupMessage="errorPopupMessage"
    :routeLink="
      $route.meta.isEdit ? 'Pre Approval Review' : 'Personal Profile Step 2'
    "
    actionButtonText="Ok"
  />
</template>

<script>
import { mapGetters } from 'vuex';
import CustomButton from './CustomButton.vue';
import RadioButton from './RadioButton.vue';
import ErrorMessagePopup from './ErrorMessagePopup.vue';

export default {
  name: 'PersonalProfileStep1',
  components: {
    CustomButton,
    RadioButton,
    ErrorMessagePopup,
  },
  computed: {
    ...mapGetters(['applicationId', 'documentListUploaded']),
    maxDate() {
      const date = new Date();
      const maxDate = new Date(
        new Date().setUTCFullYear(date.getUTCFullYear() - 18)
      );
      return `${maxDate.getUTCFullYear()}-${(
        maxDate.getUTCMonth() + 1
      ).toLocaleString('en-US', {
        minimumIntegerDigits: 2,
        useGrouping: false,
      })}-${maxDate.getUTCDate().toLocaleString('en-US', {
        minimumIntegerDigits: 2,
        useGrouping: false,
      })}`;
    },
  },
  props: {
    stepNumber: {
      type: Number,
      required: true,
    },
  },

  emits: ['completed'],
  data() {
    return {
      addressProofType: 'Aadhaar',
      params: {
        personalInfo: {
          passport: undefined,
          aadhaar: '',
          drivingLicense: undefined,
          dob: undefined,
          voterId: undefined,
        },
      },
      file: undefined,
      loading: undefined,
      frontFile: undefined,
      backFile: undefined,
      prefillData: undefined,
      isUploading: undefined,
      isUploadingFront: undefined,
      isUploadingBack: undefined,
      errorPopupMessage: undefined,
    };
  },
  mounted() {
    this.init();
  },
  methods: {
    init() {
      this.getApplicationPersonalInfo().then((result) => {
        this.params.personalInfo.aadhaar = '';
        this.prefillData = result;
        this.prefillAsPerSelectedAddressProof();
      });
    },
    prefillAsPerSelectedAddressProof() {
      if (this.addressProofType === 'Voter ID') {
        this.params.personalInfo.voterId =
          this.prefillData?.personalInfo?.voterId;
      } else if (this.addressProofType === 'Driving License') {
        this.params.personalInfo.dob = this.prefillData?.personalInfo?.dob;
        this.params.personalInfo.drivingLicense =
          this.prefillData?.personalInfo?.drivingLicense;
      } else if (this.addressProofType === 'Passport') {
        this.params.personalInfo.passport =
          this.prefillData?.personalInfo?.passport;
      }
    },
    readFile(event, params) {
      return new Promise((resolve, reject) => {
        const formData = new FormData();
        formData.append('file', event.target.files[0]);
        formData.append('jsonPayload', JSON.stringify(params));
        this.$store
          .dispatch('v3ApplicationAddressProof', {
            formData,
            applicationId: this.applicationId,
            urlParams: {
              review: !!this.$route.meta.isEdit,
            },
          })
          .then((result) => {
            this.$store.dispatch('applicationSummary', {
              applicationId: this.applicationId,
            });
            if (!result.success) {
              this.errorPopupMessage = result;
              if (
                params.personalInfo.addressProof.docSubType === 'aadhaar_front'
              ) {
                this.$refs.aadhaarFront.value = '';
              } else if (
                params.personalInfo.addressProof.docSubType === 'aadhaar_back'
              ) {
                this.$refs.aadhaarBack.value = '';
              } else if (
                params.personalInfo.addressProof.docSubType === 'passport_front'
              ) {
                this.$refs.passportFront.value = '';
              } else if (
                params.personalInfo.addressProof.docSubType === 'passport_back'
              ) {
                this.$refs.passportBack.value = '';
              } else if (
                params.personalInfo.addressProof.docSubType === 'voter_id'
              ) {
                this.$refs.voterId.value = '';
              } else if (
                params.personalInfo.addressProof.docSubType ===
                'driving_license'
              ) {
                this.$refs.drivingLicense.value = '';
              }
            } else {
              [this.file] = event.target.files;
              if (
                params.personalInfo.addressProof.docSubType ===
                'aadhaar_front' ||
                params.personalInfo.addressProof.docSubType === 'passport_front'
              ) {
                [this.frontFile] = event.target.files;
              } else if (
                params.personalInfo.addressProof.docSubType ===
                'aadhaar_back' ||
                params.personalInfo.addressProof.docSubType === 'passport_back'
              ) {
                [this.backFile] = event.target.files;
              } else {
                this.frontFile = undefined;
                this.backFile = undefined;
              }
            }
            resolve(result);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    uploadVoterId(event) {
      this.isUploading = true;
      const params = {
        personalInfo: {
          addressProof: {
            docType: 'personal_address_proof',
            docSubType: 'voter_id',
          },
        },
      };
      this.readFile(event, params)
        .then((result) => {
          this.isUploading = false;
          this.params.personalInfo.voterId = result?.idNumber;
        })
        .catch(() => {
          this.isUploading = false;
        });
    },
    uploadDrivingLicense(event) {
      this.isUploading = true;
      const params = {
        personalInfo: {
          addressProof: {
            docType: 'personal_address_proof',
            docSubType: 'driving_license',
          },
        },
      };
      this.readFile(event, params)
        .then((result) => {
          this.isUploading = false;
          this.params.personalInfo.drivingLicense = result?.idNumber;
        })
        .catch(() => {
          this.isUploading = false;
        });
    },
    uploadPassportFront(event) {
      this.isUploadingFront = true;
      const params = {
        personalInfo: {
          addressProof: {
            docType: 'personal_address_proof',
            docSubType: 'passport_front',
          },
        },
      };
      this.readFile(event, params)
        .then((result) => {
          this.isUploadingFront = false;
          this.params.personalInfo.passport = result?.idNumber;
        })
        .catch(() => {
          this.isUploadingFront = false;
        });
    },
    uploadPassportBack(event) {
      this.isUploadingBack = true;
      const params = {
        personalInfo: {
          addressProof: {
            docType: 'personal_address_proof',
            docSubType: 'passport_back',
          },
        },
      };
      this.readFile(event, params)
        .then(() => {
          this.isUploadingBack = false;
        })
        .catch(() => {
          this.isUploadingBack = false;
        });
    },
    uploadAadhaarFront(event) {
      this.isUploadingFront = true;

      const params = {
        personalInfo: {
          addressProof: {
            docType: 'personal_address_proof',
            docSubType: 'aadhaar_front',
          },
        },
      };
      this.readFile(event, params)
        .then(() => {
          this.isUploadingFront = false;
        })
        .catch(() => {
          this.isUploadingFront = false;
        });
    },
    uploadAadhaarBack(event) {
      this.isUploadingBack = true;
      const params = {
        personalInfo: {
          addressProof: {
            docType: 'personal_address_proof',
            docSubType: 'aadhaar_back',
          },
        },
      };
      this.readFile(event, params)
        .then(() => {
          this.isUploadingBack = false;
        })
        .catch(() => {
          this.isUploadingBack = false;
        });
    },
    saveAddressProofType(event) {
      this.file = undefined;
      this.frontFile = undefined;
      this.backFile = undefined;
      this.init();
      this.params = {
        personalInfo: {
          passport: undefined,
          aadhaar: '',
          drivingLicense: undefined,
          dob: undefined,
          voterId: undefined,
          addressAsPerId: {
            ownershipType: undefined,
          },
        },
      };
      this.isUploading = undefined;
      this.isUploadingFront = undefined;
      this.isUploadingBack = undefined;
      this.addressProofType = event.target.value;
      this.prefillAsPerSelectedAddressProof();
    },
    submitPersonalId() {
      this.loading = true;
      this.$store
        .dispatch('applicationPersonalId', {
          applicationId: this.applicationId,
          patchParams: this.params,
          urlParams: {
            review: !!this.$route.meta.isEdit,
          },
        })
        .then((result) => {
          this.loading = false;
          if (result.success) {
            this.$emit('completed', this.stepNumber + 1);
          } else {
            this.errorPopupMessage = result;
          }
        })
        .catch(() => {
          this.loading = false;
        });
    },
    getApplicationPersonalInfo() {
      return new Promise((resolve, reject) => {
        this.$store
          .dispatch('getApplicationPersonalInfo', {
            applicationId: this.applicationId,
          })
          .then((result) => {
            resolve(result);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
  },
};
</script>
